/* bsCategoryVocab — THE ONE balance-sheet category vocabulary.
 *
 * The BS analogue of isCategoryMatch.js's IS_CATEGORY_LABELS. Before this module the BS
 * taxonomy was spelled out as THREE separate literals that had to agree by hand:
 *   - AdminPage's `BS_CATEGORIES`      the Step-3 dropdown's display labels
 *   - AdminPage's `BS_CAT_TO_SNAKE`    display label -> the snake form stored in
 *                                      gl_line_mappings.is_category's downstream shape
 *   - inferBsMeta's `SNAKE_TO_ENGINE_CATEGORY`  snake -> the engine's five buckets
 * and, inside inferBsMeta's asset branch, a fourth spelling of the fixed/intangible split
 * as bare string literals. Nothing checked that the four agreed. The IS side has had the
 * one-list treatment since `8e66518`/`90244a6`; this is the same fix for the BS side.
 * (§6 PRINCIPLE 0 — one owner per derived thing. Measured in diffs/b5-phase0.txt.)
 *
 * ONE ROW PER CATEGORY names everything about it:
 *   label    — what the OPERATOR sees and picks at Step 3, and what the canonical BS
 *              template carries verbatim in its is_category column.
 *   snake    — the intermediate slug the wizard writes while assembling a leaf.
 *   engine   — which of the engine's FIVE buckets it resolves to. ★ Note that
 *              'Fixed Assets' and 'Intangible Assets' BOTH resolve to
 *              'non_current_assets': the engine has ONE asset category and carries the
 *              fixed/intangible distinction as a SUB-TYPE. A leaf that reaches
 *              buildBSLines still carrying a snake (rather than an engine category) is
 *              silently dropped from the balance sheet — that is what Session A had to
 *              hand-correct on cid=3's seven asset rows.
 *   subType  — the fixed/intangible sub-type, for the two asset categories; null
 *              elsewhere. This is `bs_actuals_meta.subcategory` for asset leaves, and it
 *              is what buildBSLines keys its Fixed / Intangible section split on.
 *
 * ⛔ The engine's own five-value enum lives in engines/lineCategories.js (BS_CATEGORIES)
 * and STAYS there — this vocabulary RESOLVES TO it, it does not restate it. Likewise the
 * four asset ROLES below are re-exported from BS_ROLES rather than respelled: a fifth
 * hardcoded map is precisely the defect this module removes.
 */
import { BS_ROLES } from '../engines/lineCategories';

export const BS_CATEGORY_VOCAB = [
    { label: 'Current Assets',      snake: 'current_assets',      engine: 'current_assets',          subType: null },
    { label: 'Fixed Assets',        snake: 'fixed_assets',        engine: 'non_current_assets',      subType: 'fixed_assets' },
    { label: 'Intangible Assets',   snake: 'intangible_assets',   engine: 'non_current_assets',      subType: 'intangible_assets' },
    { label: 'Current Liabilities', snake: 'current_liabilities', engine: 'current_liabilities',     subType: null },
    { label: 'LT Liabilities',      snake: 'lt_liabilities',      engine: 'non_current_liabilities', subType: null },
    { label: 'Equity',              snake: 'equity',              engine: 'equity',                  subType: null },
];

/* The six real category labels, in statement order. */
export const BS_CATEGORY_LABELS = BS_CATEGORY_VOCAB.map(v => v.label);

/* The Step-3 dropdown list. 'Skip' leads, exactly as the IS side does
   (`IS_CATEGORIES = ['Skip', ...IS_CATEGORY_LABELS]`). ★ Named _OPTIONS, not
   _CATEGORIES: `BS_CATEGORIES` is ALREADY TAKEN by the engine's five-value enum in
   engines/lineCategories.js, and the two vocabularies are different sizes and different
   values. Two live constants sharing one name across two modules is a hazard in itself. */
export const BS_CATEGORY_OPTIONS = ['Skip', ...BS_CATEGORY_LABELS];

/* Operator display label -> snake. Was AdminPage's BS_CAT_TO_SNAKE. */
export const BS_DISPLAY_TO_SNAKE = Object.fromEntries(BS_CATEGORY_VOCAB.map(v => [v.label, v.snake]));

/* Snake -> the engine's five buckets. Was inferBsMeta's SNAKE_TO_ENGINE_CATEGORY.
   Key ORDER is load-bearing in one place: inferBsMetaLeaf's fail-loud message lists
   `Object.keys(...)`, so the array order above is the order the operator is shown. */
export const BS_SNAKE_TO_ENGINE_CATEGORY = Object.fromEntries(BS_CATEGORY_VOCAB.map(v => [v.snake, v.engine]));

/* Snake -> fixed/intangible sub-type. Only the two asset categories have one. */
export const BS_SNAKE_TO_SUBTYPE = Object.fromEntries(
    BS_CATEGORY_VOCAB.filter(v => v.subType).map(v => [v.snake, v.subType])
);

/* Sub-type -> its two roles. THE FOUR EXISTING ROLES, re-exported from BS_ROLES rather
   than respelled. `gross` is the asset itself; `contra` is its accumulated-depreciation /
   -amortization line.
   ★ WHICH OF THE TWO A LEAF IS, IS AN OPERATOR ANSWER — NOT A NAME TEST. This comment used
   to say the split "remains a NAME TEST inside inferBsMeta today (/accum/i)". B5 s5
   (`ff98f62`) RETIRED that: gross-vs-contra is now answered on the Step-4a asset panel,
   rides the same `classification` channel the current-asset panel uses, BLOCKS the import
   while unanswered, and THROWS by name if an unanswered leaf ever reaches the classifier.
   ⛔ DO NOT REINTRODUCE A NAME TEST HERE. The role selects the `bs_assumptions` formula and
   the two formulas move in OPPOSITE directions — `accum_depr`/`accum_amort` SUBTRACT
   depreciation, `fa_gross`/`ia_gross` ADD additions — so a tenant naming its line
   "Reserve for Depreciation" was classified a gross asset and its balance sheet broke by the
   cumulative depreciation, silently and growing ($32,483.97 over nine months, measured).
   What THIS module contributes is that the role STRINGS are named once. */
export const BS_ASSET_ROLES = {
    fixed_assets:      { gross: BS_ROLES.FIXED_ASSET,      contra: BS_ROLES.ACCUMULATED_DEPRECIATION },
    intangible_assets: { gross: BS_ROLES.INTANGIBLE_ASSET, contra: BS_ROLES.ACCUMULATED_AMORTIZATION },
};
